// purchase_dashboard.jsx - Professional Accounts Manager Style with Enhanced Product Management
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../../components/backbutton";
import ProductModal from "../../components/ProductModal";
import ScreenHeader from "../../components/ScreenHeader";
import {
  LogOut,
  FileText,
  Package,
  ShoppingCart,
  List,
  Calendar,
  DollarSign,
  Users,
  UserPlus,
  Truck,
  PlusCircle,
  Eye,
  Edit,
  Trash2,
  FolderPlus,
  Layers,
  CheckCircle,
  Search,
  Filter,
  X,
  Link2
} from "lucide-react";
import "./purchase_dashboard.css";

const PAGE_SIZE = 10;

// Tooltip Action Button Component
const ActionIconButton = ({ icon, label, onClick, colorClass }) => (
  <button className={`action-btn ${colorClass}`} onClick={onClick} style={{ position: 'relative' }}>
    {icon}
    <span className="tooltip-text">{label}</span>
  </button>
);

// Tooltip Button Component for regular buttons
const TooltipButton = ({ children, label, onClick, className, disabled = false, title = "" }) => (
  <button 
    className={className} 
    onClick={onClick} 
    disabled={disabled}
    title={title}
    style={{ position: 'relative' }}
  >
    {children}
    <span className="tooltip-text">{label}</span>
  </button>
);

// Custom Pagination component - only shows when totalItems > PAGE_SIZE
const CustomPagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalItems <= PAGE_SIZE) {
    return null;
  }

  return (
    <div className="pagination-container">
      <div className="pagination-summary">
        Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems} entries
      </div>
      <div className="pagination-buttons">
        <TooltipButton
          className="pagination-button"
          label="Previous Page"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Prev
        </TooltipButton>
        
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
            if (pageNum > 4 && i === 4) {
              return (
                <button key="ellipsis-end" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
          } else if (currentPage >= totalPages - 2) {
            const startPage = totalPages - 4;
            pageNum = startPage + i;
            if (i === 0 && startPage > 1) {
              return (
                <button key="ellipsis-start" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
          } else {
            const startPage = currentPage - 2;
            pageNum = startPage + i;
            if (i === 0 && startPage > 1) {
              return (
                <button key="ellipsis-start" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
            if (i === 4 && startPage + 4 < totalPages) {
              return (
                <button key="ellipsis-end" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
          }
          
          if (pageNum > totalPages || pageNum < 1) return null;
          
          return (
            <TooltipButton
              key={pageNum}
              className={`pagination-button ${currentPage === pageNum ? 'active' : ''}`}
              label={`Go to page ${pageNum}`}
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </TooltipButton>
          );
        })}
        
        <TooltipButton
          className="pagination-button"
          label="Next Page"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </TooltipButton>
      </div>
    </div>
  );
};

export default function PurchaseDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("create-ledger");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  // Pagination states
  const [customerPage, setCustomerPage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [categoryPage, setCategoryPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [groupPage, setGroupPage] = useState(1);
  const [schedulePage, setSchedulePage] = useState(1);
  const [poPage, setPoPage] = useState(1);
  const [grnPage, setGrnPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);

  // Modal states
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showCreateSupplier, setShowCreateSupplier] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showSubProductModal, setShowSubProductModal] = useState(false);
  const [currentParentProduct, setCurrentParentProduct] = useState(null);
  const [showRawMaterialModal, setShowRawMaterialModal] = useState(false);

  // Validation attempt flags
  const [customerSubmitAttempted, setCustomerSubmitAttempted] = useState(false);
  const [supplierSubmitAttempted, setSupplierSubmitAttempted] = useState(false);
  const [categorySubmitAttempted, setCategorySubmitAttempted] = useState(false);
  const [productSubmitAttempted, setProductSubmitAttempted] = useState(false);
  const [groupSubmitAttempted, setGroupSubmitAttempted] = useState(false);
  const [scheduleSubmitAttempted, setScheduleSubmitAttempted] = useState(false);
  const [poSubmitAttempted, setPoSubmitAttempted] = useState(false);
  const [paymentSubmitAttempted, setPaymentSubmitAttempted] = useState(false);

  // Data states
  const [customers, setCustomers] = useState([
    { id: 1, pocName: "Santhosh", companyName: "ABC Company", username: "seller@smartseller.com", password: "pass123", address: "Chennai", pan: "AAAAA0000A", bankDetails: "HDFC Bank", gst: "22AAAAA0000A1Z", vendorCode: "CUST-001" }
  ]);
  const [suppliers, setSuppliers] = useState([
    { id: 1, pocName: "SK", companyName: "SK Company", username: "sk@sk.com", password: "pass456", address: "Coimbatore", pan: "BBBBB0000B", bankDetails: "ICICI Bank", gst: "33BBBBB0000B2Z", supplierCode: "SUP-001" }
  ]);
  const [categories, setCategories] = useState([
    { id: 1, name: "Electronics", description: "Electronic components" },
    { id: 2, name: "Raw Materials", description: "Raw materials for production" }
  ]);
  const [rawMaterials, setRawMaterials] = useState([
    { id: 1, name: "Plastic", type: "Raw Material", unit: "kg", cost: 50 },
    { id: 2, name: "Copper Wire", type: "Raw Material", unit: "meter", cost: 120 },
    { id: 3, name: "Steel", type: "Raw Material", unit: "kg", cost: 80 },
    { id: 4, name: "Aluminum", type: "Raw Material", unit: "kg", cost: 90 }
  ]);

  const [products, setProducts] = useState([
    { 
      id: 1, 
      name: "Keyboard", 
      code: "KB-001", 
      description: "Mechanical keyboard", 
      category: "Electronics", 
      dimension: "450x150mm", 
      cost: 1500,
      image: null,
      _3dFile: null,
      extraDimension: null,
      totalDimension: "450x150mm",
      childProducts: [],
      rawMaterials: []
    }
  ]);
  
  const [groups, setGroups] = useState([
    { id: 1, name: "Accessories Group", description: "All accessories", products: [{ id: 1, name: "Keyboard" }] }
  ]);
  const [schedules, setSchedules] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([
    {
      id: 1, poNumber: "PO-001", customer: { companyName: "ABC Company", id: 1 }, supplier: { companyName: "SK Company", id: 1 },
      orderDate: "2024-01-15", deliveryDate: "2024-01-30", totalAmount: 50000, status: "Pending",
      selectedProducts: [{ name: "Keyboard", quantity: 10, cost: 1500 }],
      rawMaterial: [{ name: "Plastic", quantity: 100 }]
    }
  ]);
  const [grns, setGrns] = useState([
    { id: 1, grnNumber: "GRN-001", poNumber: "PO-001", supplier: "SK Company", product: "Keyboard", quantity: 10, supplierInvoiceNo: "INV-001", status: "Pending" }
  ]);
  const [payments, setPayments] = useState([
    { id: 1, paymentId: "PAY-001", grnId: "GRN-001", amount: 50000, paymentDate: "2024-01-20", paymentMode: "Bank Transfer", utrNumber: "UTR123456", verified: false }
  ]);

  // Enhanced Product Form State
  const [productForm, setProductForm] = useState({
    name: '',
    code: '',
    description: '',
    category: '',
    dimension: '',
    extraDimension: '',
    totalDimension: '',
    cost: '',
    image: null,
    _3dFile: null,
    imagePreview: null,
    _3dPreview: null
  });
  
  // Child product form state
  const [childProductForm, setChildProductForm] = useState({
    name: '',
    code: '',
    description: '',
    quantity: 1,
    rawMaterials: []
  });
  
  // Raw material assignment state
  const [rawMaterialAssignment, setRawMaterialAssignment] = useState({
    productId: null,
    rawMaterialId: '',
    quantity: 1,
    area: 0
  });

  // Stock data
  const [stockData] = useState({
    finishedGoods: [{ name: 'Keyboard', quantity: 100 }],
    productionPending: [{ name: 'Keyboard', quantity: 50 }],
    outsourcePending: [{ name: 'Keyboard', quantity: 30 }],
    rawMaterial: [{ name: 'Plastic', quantity: 500 }]
  });

  // Validation functions
  const validateCustomerForm = () => {
    const errors = {};
    if (!customerForm.pocName?.trim()) errors.pocName = 'POC Name is required';
    if (!customerForm.companyName?.trim()) errors.companyName = 'Company Name is required';
    if (!customerForm.username?.trim()) errors.username = 'Username is required';
    if (!customerForm.password?.trim()) errors.password = 'Password is required';
    if (!customerForm.address?.trim()) errors.address = 'Address is required';
    return errors;
  };

  const validateSupplierForm = () => {
    const errors = {};
    if (!supplierForm.pocName?.trim()) errors.pocName = 'POC Name is required';
    if (!supplierForm.companyName?.trim()) errors.companyName = 'Company Name is required';
    if (!supplierForm.username?.trim()) errors.username = 'Username is required';
    if (!supplierForm.password?.trim()) errors.password = 'Password is required';
    if (!supplierForm.address?.trim()) errors.address = 'Address is required';
    return errors;
  };

  const validateCategoryForm = () => {
    const errors = {};
    if (!categoryForm.name?.trim()) errors.name = 'Category Name is required';
    return errors;
  };

  const validateProductForm = () => {
    const errors = {};
    if (!productForm.name?.trim()) errors.name = 'Product Name is required';
    if (!productForm.code?.trim()) errors.code = 'Product Code is required';
    if (!productForm.category) errors.category = 'Category is required';
    if (!productForm.cost || productForm.cost <= 0) errors.cost = 'Valid Cost is required';
    return errors;
  };

  const validateGroupForm = () => {
    const errors = {};
    if (!groupForm.name?.trim()) errors.name = 'Group Name is required';
    if (groupForm.products.length === 0) errors.products = 'At least one product must be selected';
    return errors;
  };

  const validateScheduleForm = () => {
    const errors = {};
    if (!scheduleForm.category) errors.category = 'Category is required';
    if (!scheduleForm.customer) errors.customer = 'Customer is required';
    if (!scheduleForm.productId) errors.productId = 'Product is required';
    if (!scheduleForm.quantity || scheduleForm.quantity <= 0) errors.quantity = 'Valid Quantity is required';
    if (!scheduleForm.frequency) errors.frequency = 'Frequency is required';
    if (!scheduleForm.validTill) errors.validTill = 'Valid Till date is required';
    return errors;
  };

  const validatePOForm = () => {
    const errors = {};
    if (!poForm.customerId) errors.customerId = 'Customer is required';
    if (!poForm.supplierId) errors.supplierId = 'Supplier is required';
    if (!poForm.deliveryDate) errors.deliveryDate = 'Delivery Date is required';
    if (poForm.selectedProducts.length === 0) errors.selectedProducts = 'At least one product is required';
    return errors;
  };

  const validatePaymentForm = () => {
    const errors = {};
    if (!paymentForm.grnId) errors.grnId = 'GRN is required';
    if (!paymentForm.amount || paymentForm.amount <= 0) errors.amount = 'Valid Amount is required';
    if (!paymentForm.paymentMode) errors.paymentMode = 'Payment Mode is required';
    if (!paymentForm.utrNumber?.trim()) errors.utrNumber = 'UTR Number is required';
    return errors;
  };

  // Error getters
  const getCustomerError = (field) => {
    if (!customerSubmitAttempted) return null;
    return validateCustomerForm()[field];
  };

  const getSupplierError = (field) => {
    if (!supplierSubmitAttempted) return null;
    return validateSupplierForm()[field];
  };

  const getCategoryError = (field) => {
    if (!categorySubmitAttempted) return null;
    return validateCategoryForm()[field];
  };

  const getProductError = (field) => {
    if (!productSubmitAttempted) return null;
    return validateProductForm()[field];
  };

  const getGroupError = (field) => {
    if (!groupSubmitAttempted) return null;
    return validateGroupForm()[field];
  };

  const getScheduleError = (field) => {
    if (!scheduleSubmitAttempted) return null;
    return validateScheduleForm()[field];
  };

  const getPOError = (field) => {
    if (!poSubmitAttempted) return null;
    return validatePOForm()[field];
  };

  const getPaymentError = (field) => {
    if (!paymentSubmitAttempted) return null;
    return validatePaymentForm()[field];
  };

  // Form states
  const [customerForm, setCustomerForm] = useState({
    pocName: '', companyName: '', username: '', password: '',
    address: '', pan: '', bankDetails: '', gst: '',
    vendorCode: 'CUST-' + Date.now()
  });
  const [supplierForm, setSupplierForm] = useState({
    pocName: '', companyName: '', username: '', password: '',
    address: '', pan: '', bankDetails: '', gst: '',
    supplierCode: 'SUP-' + Date.now()
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [groupForm, setGroupForm] = useState({ name: '', description: '', products: [] });
  const [scheduleForm, setScheduleForm] = useState({
    category: '', subCategory: '', group: '', customer: '',
    productId: '', quantity: '', frequency: 'Weekly', validTill: ''
  });
  const [poForm, setPoForm] = useState({
    poNumber: 'PO-' + Date.now(), customerId: '', scheduleId: '',
    supplierId: '', orderDate: new Date().toISOString().split('T')[0],
    deliveryDate: '', selectedProducts: [], totalAmount: 0, status: 'Pending'
  });
  const [paymentForm, setPaymentForm] = useState({
    paymentId: 'PAY-' + Date.now(), grnId: '', amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Bank Transfer', utrNumber: '', verified: false
  });

  // Extra fields states
  const [customerExtraFields, setCustomerExtraFields] = useState([]);
  const [supplierExtraFields, setSupplierExtraFields] = useState([]);
  const [productExtraFields, setProductExtraFields] = useState([]);

  // Submit handlers
  const handleCreateCustomer = () => {
    setCustomerSubmitAttempted(true);
    if (Object.keys(validateCustomerForm()).length > 0) return;
    const newCustomer = { id: Date.now(), ...customerForm, extraFields: customerExtraFields, createdAt: new Date().toISOString() };
    setCustomers([...customers, newCustomer]);
    handleCloseCustomerModal();
    alert('Customer created successfully!');
  };

  const handleCreateSupplier = () => {
    setSupplierSubmitAttempted(true);
    if (Object.keys(validateSupplierForm()).length > 0) return;
    const newSupplier = { id: Date.now(), ...supplierForm, extraFields: supplierExtraFields, createdAt: new Date().toISOString() };
    setSuppliers([...suppliers, newSupplier]);
    handleCloseSupplierModal();
    alert('Supplier created successfully!');
  };

  const handleCreateCategory = () => {
    setCategorySubmitAttempted(true);
    if (Object.keys(validateCategoryForm()).length > 0) return;
    const newCategory = { id: Date.now(), name: categoryForm.name, description: categoryForm.description || 'N/A' };
    setCategories([...categories, newCategory]);
    setShowCreateCategory(false);
    setCategorySubmitAttempted(false);
    setCategoryForm({ name: '', description: '' });
    alert('Category created successfully!');
  };

  const handleFileChange = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === 'image') {
          setProductForm(prev => ({ ...prev, image: file, imagePreview: reader.result }));
        } else if (field === '3d') {
          setProductForm(prev => ({ ...prev, _3dFile: file, _3dPreview: reader.result }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const calculateTotalDimension = () => {
    const dim = parseFloat(productForm.dimension) || 0;
    const extraDim = parseFloat(productForm.extraDimension) || 0;
    const total = dim + extraDim;
    setProductForm(prev => ({ ...prev, totalDimension: total.toString() }));
  };

  const handleCreateProduct = () => {
    setProductSubmitAttempted(true);
    if (Object.keys(validateProductForm()).length > 0) return;
    
    const newProduct = { 
      id: Date.now(), 
      ...productForm, 
      childProducts: [],
      rawMaterials: [],
      extraFields: productExtraFields,
      createdAt: new Date().toISOString() 
    };
    setProducts([...products, newProduct]);
    setShowCreateProduct(false);
    setProductSubmitAttempted(false);
    setProductForm({ 
      name: '', code: '', description: '', category: '', 
      dimension: '', extraDimension: '', totalDimension: '', cost: '',
      image: null, _3dFile: null, imagePreview: null, _3dPreview: null 
    });
    setProductExtraFields([]);
    alert('Product created successfully!');
  };

  const handleAddChildProduct = () => {
    if (!childProductForm.name.trim()) {
      alert('Please enter child product name');
      return;
    }
    
    const newChildProduct = {
      id: Date.now(),
      ...childProductForm,
      createdAt: new Date().toISOString()
    };
    
    const updatedProducts = products.map(p => {
      if (p.id === currentParentProduct) {
        return {
          ...p,
          childProducts: [...(p.childProducts || []), newChildProduct]
        };
      }
      return p;
    });
    
    setProducts(updatedProducts);
    setChildProductForm({ name: '', code: '', description: '', quantity: 1, rawMaterials: [] });
    setShowSubProductModal(false);
    alert('Child product added successfully!');
  };

  const handleAssignRawMaterial = () => {
    if (!rawMaterialAssignment.rawMaterialId) {
      alert('Please select a raw material');
      return;
    }
    
    const selectedRM = rawMaterials.find(rm => rm.id === parseInt(rawMaterialAssignment.rawMaterialId));
    if (!selectedRM) return;
    
    const newAssignment = {
      id: Date.now(),
      rawMaterial: selectedRM,
      quantity: rawMaterialAssignment.quantity,
      area: rawMaterialAssignment.area,
      productId: rawMaterialAssignment.productId
    };
    
    const updatedProducts = products.map(p => {
      if (p.id === rawMaterialAssignment.productId) {
        return {
          ...p,
          rawMaterials: [...(p.rawMaterials || []), newAssignment]
        };
      }
      return p;
    });
    
    setProducts(updatedProducts);
    setRawMaterialAssignment({ productId: null, rawMaterialId: '', quantity: 1, area: 0 });
    setShowRawMaterialModal(false);
    alert('Raw material assigned successfully!');
  };

  const handleRemoveChildProduct = (productId, childId) => {
    if (window.confirm('Remove this child product?')) {
      const updatedProducts = products.map(p => {
        if (p.id === productId) {
          return {
            ...p,
            childProducts: p.childProducts.filter(c => c.id !== childId)
          };
        }
        return p;
      });
      setProducts(updatedProducts);
    }
  };

  const handleRemoveRawMaterial = (productId, rmId) => {
    if (window.confirm('Remove this raw material assignment?')) {
      const updatedProducts = products.map(p => {
        if (p.id === productId) {
          return {
            ...p,
            rawMaterials: p.rawMaterials.filter(rm => rm.id !== rmId)
          };
        }
        return p;
      });
      setProducts(updatedProducts);
    }
  };

  const handleCreateGroup = () => {
    setGroupSubmitAttempted(true);
    if (Object.keys(validateGroupForm()).length > 0) return;
    const selectedProducts = products.filter(p => groupForm.products.includes(p.id.toString()));
    const newGroup = { id: Date.now(), name: groupForm.name, description: groupForm.description || 'N/A', products: selectedProducts };
    setGroups([...groups, newGroup]);
    setShowCreateGroup(false);
    setGroupSubmitAttempted(false);
    setGroupForm({ name: '', description: '', products: [] });
    alert('Group created successfully!');
  };

  const handleCreateSchedule = () => {
    setScheduleSubmitAttempted(true);
    if (Object.keys(validateScheduleForm()).length > 0) return;
    const selectedProduct = products.find(p => p.id === parseInt(scheduleForm.productId));
    const newSchedule = {
      id: Date.now(), ...scheduleForm,
      product: selectedProduct,
      productName: selectedProduct?.name || 'N/A',
      quantity: parseInt(scheduleForm.quantity),
      createdAt: new Date().toISOString()
    };
    setSchedules([...schedules, newSchedule]);
    setScheduleSubmitAttempted(false);
    setScheduleForm({ category: '', subCategory: '', group: '', customer: '', productId: '', quantity: '', frequency: 'Weekly', validTill: '' });
    alert('Schedule created successfully!');
  };

  const handleCreatePO = () => {
    setPoSubmitAttempted(true);
    if (Object.keys(validatePOForm()).length > 0) return;
    const selectedCustomer = customers.find(c => c.id === parseInt(poForm.customerId));
    const selectedSupplier = suppliers.find(s => s.id === parseInt(poForm.supplierId));
    const newPO = {
      id: Date.now(),
      poNumber: poForm.poNumber,
      customer: selectedCustomer,
      supplier: selectedSupplier,
      selectedProducts: poForm.selectedProducts,
      orderDate: poForm.orderDate,
      deliveryDate: poForm.deliveryDate,
      totalAmount: poForm.totalAmount,
      status: 'Pending',
      rawMaterial: stockData.rawMaterial,
      createdAt: new Date().toISOString()
    };
    setPurchaseOrders([...purchaseOrders, newPO]);
    setPoSubmitAttempted(false);
    setPoForm({
      poNumber: 'PO-' + Date.now(), customerId: '', scheduleId: '',
      supplierId: '', orderDate: new Date().toISOString().split('T')[0],
      deliveryDate: '', selectedProducts: [], totalAmount: 0, status: 'Pending'
    });
    alert('Purchase Order created successfully!');
  };

  const handleMakePayment = () => {
    setPaymentSubmitAttempted(true);
    if (Object.keys(validatePaymentForm()).length > 0) return;
    const newPayment = {
      id: Date.now(),
      paymentId: paymentForm.paymentId,
      grnId: paymentForm.grnId,
      amount: parseFloat(paymentForm.amount),
      paymentDate: paymentForm.paymentDate,
      paymentMode: paymentForm.paymentMode,
      utrNumber: paymentForm.utrNumber,
      verified: paymentForm.verified,
      createdAt: new Date().toISOString()
    };
    setPayments([...payments, newPayment]);
    setPaymentSubmitAttempted(false);
    setPaymentForm({
      paymentId: 'PAY-' + Date.now(), grnId: '', amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: 'Bank Transfer', utrNumber: '', verified: false
    });
    alert('Payment recorded successfully!');
  };

  const handleAddProductToPO = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product && !poForm.selectedProducts.find(p => p.id === productId)) {
      setPoForm({
        ...poForm,
        selectedProducts: [...poForm.selectedProducts, { ...product, quantity: 1 }],
        totalAmount: poForm.totalAmount + (product.cost || 0)
      });
    }
  };

  const handleUpdateProductQuantity = (productId, quantity) => {
    const updatedProducts = poForm.selectedProducts.map(p =>
      p.id === productId ? { ...p, quantity: parseInt(quantity) || 0 } : p
    );
    const total = updatedProducts.reduce((sum, p) => sum + ((p.cost || 0) * (p.quantity || 0)), 0);
    setPoForm({ ...poForm, selectedProducts: updatedProducts, totalAmount: total });
  };

  const handleRemoveProductFromPO = (productId) => {
    const updatedProducts = poForm.selectedProducts.filter(p => p.id !== productId);
    const total = updatedProducts.reduce((sum, p) => sum + ((p.cost || 0) * (p.quantity || 0)), 0);
    setPoForm({ ...poForm, selectedProducts: updatedProducts, totalAmount: total });
  };

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  const handleCloseCustomerModal = () => {
    setShowCreateCustomer(false);
    setCustomerSubmitAttempted(false);
    setCustomerExtraFields([]);
    setCustomerForm({
      pocName: '', companyName: '', username: '', password: '',
      address: '', pan: '', bankDetails: '', gst: '',
      vendorCode: 'CUST-' + Date.now()
    });
  };

  const handleCloseSupplierModal = () => {
    setShowCreateSupplier(false);
    setSupplierSubmitAttempted(false);
    setSupplierExtraFields([]);
    setSupplierForm({
      pocName: '', companyName: '', username: '', password: '',
      address: '', pan: '', bankDetails: '', gst: '',
      supplierCode: 'SUP-' + Date.now()
    });
  };

  // Extra fields handlers
  const addExtraField = (type) => {
    if (type === 'customer') setCustomerExtraFields([...customerExtraFields, { name: '', value: '' }]);
    if (type === 'supplier') setSupplierExtraFields([...supplierExtraFields, { name: '', value: '' }]);
    if (type === 'product') setProductExtraFields([...productExtraFields, { name: '', value: '' }]);
  };

  const updateExtraField = (type, index, field, value) => {
    if (type === 'customer') {
      const updated = [...customerExtraFields];
      updated[index][field] = value;
      setCustomerExtraFields(updated);
    }
    if (type === 'supplier') {
      const updated = [...supplierExtraFields];
      updated[index][field] = value;
      setSupplierExtraFields(updated);
    }
    if (type === 'product') {
      const updated = [...productExtraFields];
      updated[index][field] = value;
      setProductExtraFields(updated);
    }
  };

  const removeExtraField = (type, index) => {
    if (type === 'customer') setCustomerExtraFields(customerExtraFields.filter((_, i) => i !== index));
    if (type === 'supplier') setSupplierExtraFields(supplierExtraFields.filter((_, i) => i !== index));
    if (type === 'product') setProductExtraFields(productExtraFields.filter((_, i) => i !== index));
  };

  // Helper functions
  const formatValue = (value) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  };

  const filteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        if (value === undefined || value === null) return false;
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  };

  const paginate = (data, page) => data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Get child products count
  const getChildProductsCount = (product) => {
    return product.childProducts?.length || 0;
  };

  const getRawMaterialsCount = (product) => {
    return product.rawMaterials?.length || 0;
  };

  // Sidebar menu items
  const tabItems = [
    { id: "create-ledger", label: "Create Ledger", icon: <FileText size={18} /> },
    { id: "product", label: "Product", icon: <Package size={18} /> },
    { id: "create-po", label: "Create PO", icon: <ShoppingCart size={18} /> },
    { id: "list-po", label: "List PO", icon: <List size={18} /> },
    { id: "list-grn", label: "List GRN", icon: <List size={18} /> },
    { id: "supplier-payment", label: "Supplier Payment", icon: <DollarSign size={18} /> },
    { id: "list-schedule", label: "List Schedule", icon: <Calendar size={18} /> }
  ];

  // ==================== RENDER FUNCTIONS ====================

  const renderCreateLedger = () => (
    <div>
      <div className="create-cards-grid">
        <div className="create-card" onClick={() => setShowCreateCustomer(true)}>
          <div className="create-card-icon"><UserPlus size={28} /></div>
          <h3>Create Customer</h3>
          <p>Add new customer to the system with complete details</p>
          <TooltipButton className="create-card-btn" label="Create Customer" onClick={() => setShowCreateCustomer(true)}>
            <PlusCircle size={16} /> Create Customer
          </TooltipButton>
        </div>

        <div className="create-card" onClick={() => setShowCreateSupplier(true)} style={{ background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)" }}>
          <div className="create-card-icon"><Truck size={28} /></div>
          <h3>Create Supplier</h3>
          <p>Add new supplier to the system with complete details</p>
          <TooltipButton className="create-card-btn" label="Create Supplier" onClick={() => setShowCreateSupplier(true)}>
            <PlusCircle size={16} /> Create Supplier
          </TooltipButton>
        </div>
      </div>

      {/* List Customer Section */}
      <div className="list-section">
        <h3><Users size={18} /> List Customer ({customers.length})</h3>
        {customers.length === 0 ? (
          <div className="empty-state"><p>No customers yet. Create a customer to see it here.</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>POC Name</th><th>Company Name</th><th>Username</th><th>Address</th><th>PAN</th><th>Bank Details</th><th>GST</th><th>Vendor Code</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredData(customers, ['pocName', 'companyName', 'username']), customerPage).map(customer => (
                    <tr key={customer.id}>
                      <td className="highlight">{formatValue(customer.pocName)}</td>
                      <td>{formatValue(customer.companyName)}</td>
                      <td>{formatValue(customer.username)}</td>
                      <td>{formatValue(customer.address)}</td>
                      <td>{formatValue(customer.pan)}</td>
                      <td>{formatValue(customer.bankDetails)}</td>
                      <td>{formatValue(customer.gst)}</td>
                      <td>{formatValue(customer.vendorCode)}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(customer, null, 2))} colorClass="view" />
                        <ActionIconButton icon={<Edit size={16} />} label="Edit Customer" onClick={() => alert('Edit customer: ' + customer.companyName)} colorClass="edit" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination 
              currentPage={customerPage} 
              totalPages={Math.ceil(filteredData(customers, ['pocName', 'companyName', 'username']).length / PAGE_SIZE)} 
              onPageChange={setCustomerPage} 
              totalItems={filteredData(customers, ['pocName', 'companyName', 'username']).length} 
            />
          </>
        )}
      </div>

      {/* List Supplier Section */}
      <div className="list-section">
        <h3><Truck size={18} /> List Supplier ({suppliers.length})</h3>
        {suppliers.length === 0 ? (
          <div className="empty-state"><p>No suppliers yet. Create a supplier to see it here.</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>POC Name</th><th>Company Name</th><th>Username</th><th>Address</th><th>PAN</th><th>Bank Details</th><th>GST</th><th>Supplier Code</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredData(suppliers, ['pocName', 'companyName', 'username']), supplierPage).map(supplier => (
                    <tr key={supplier.id}>
                      <td className="highlight">{formatValue(supplier.pocName)}</td>
                      <td>{formatValue(supplier.companyName)}</td>
                      <td>{formatValue(supplier.username)}</td>
                      <td>{formatValue(supplier.address)}</td>
                      <td>{formatValue(supplier.pan)}</td>
                      <td>{formatValue(supplier.bankDetails)}</td>
                      <td>{formatValue(supplier.gst)}</td>
                      <td>{formatValue(supplier.supplierCode)}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(supplier, null, 2))} colorClass="view" />
                        <ActionIconButton icon={<Edit size={16} />} label="Edit Supplier" onClick={() => alert('Edit supplier: ' + supplier.companyName)} colorClass="edit" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination 
              currentPage={supplierPage} 
              totalPages={Math.ceil(filteredData(suppliers, ['pocName', 'companyName', 'username']).length / PAGE_SIZE)} 
              onPageChange={setSupplierPage} 
              totalItems={filteredData(suppliers, ['pocName', 'companyName', 'username']).length} 
            />
          </>
        )}
      </div>
    </div>
  );

  const renderProduct = () => (
    <div>
      <div className="create-cards-grid">
        <div className="create-card" onClick={() => setShowCreateCategory(true)}>
          <div className="create-card-icon"><FolderPlus size={28} /></div>
          <h3>Create Category</h3>
          <p>Add new product category</p>
          <TooltipButton className="create-card-btn" label="Create Category" onClick={() => setShowCreateCategory(true)}>
            <PlusCircle size={16} /> Create Category
          </TooltipButton>
        </div>

        <div className="create-card" onClick={() => setShowCreateProduct(true)} style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
          <div className="create-card-icon"><Package size={28} /></div>
          <h3>Create Product</h3>
          <p>Add new product with details, child products & raw materials</p>
          <TooltipButton className="create-card-btn" label="Create Product" onClick={() => setShowCreateProduct(true)}>
            <PlusCircle size={16} /> Create Product
          </TooltipButton>
        </div>

        <div className="create-card" onClick={() => setShowCreateGroup(true)} style={{ background: "linear-gradient(135deg, rgb(245, 158, 11) 0%, rgb(234, 88, 12) 100%)" }}> 
          <div className="create-card-icon"><Layers size={28} /></div>
          <h3>Create Group</h3>
          <p>Group products together</p>
          <TooltipButton className="create-card-btn" label="Create Group" onClick={() => setShowCreateGroup(true)}>
            <PlusCircle size={16} /> Create Group
          </TooltipButton>
        </div>
      </div>

      {/* List Category */}
      <div className="list-section">
        <h3><FolderPlus size={18} /> List Category ({categories.length})</h3>
        <div className="data-table-container">
          <table className="data-table">
            <thead><tr><th>Category Name</th><th>Description</th><th>Actions</th></tr></thead>
            <tbody>
              {paginate(categories, categoryPage).map(cat => (
                <tr key={cat.id}>
                  <td className="highlight">{formatValue(cat.name)}</td>
                  <td>{formatValue(cat.description)}</td>
                  <td className="action-buttons">
                    <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(`Category: ${cat.name}`)} colorClass="view" />
                    <ActionIconButton icon={<Edit size={16} />} label="Edit Category" onClick={() => alert('Edit category: ' + cat.name)} colorClass="edit" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CustomPagination 
          currentPage={categoryPage} 
          totalPages={Math.ceil(categories.length / PAGE_SIZE)} 
          onPageChange={setCategoryPage} 
          totalItems={categories.length} 
        />
      </div>

      {/* List Product with Child Products and Raw Materials */}
      <div className="list-section">
        <h3><Package size={18} /> List Product ({products.length})</h3>
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product Name</th><th>Code</th><th>Description</th><th>Category</th><th>Dimension</th><th>Cost</th>
                <th>Child Products</th><th>Raw Materials</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginate(products, productPage).map(prod => (
                <tr key={prod.id}>
                  <td className="highlight">{formatValue(prod.name)}</td>
                  <td>{formatValue(prod.code)}</td>
                  <td>{formatValue(prod.description)}</td>
                  <td>{formatValue(prod.category)}</td>
                  <td>{formatValue(prod.dimension)}</td>
                  <td className="amount">₹{formatValue(prod.cost)}</td>
                  <td className="product-child-actions">
                    <div className="product-action-group">
                      <div className="product-action-header-column">
                        <span className="product-action-count">
                          📦 {getChildProductsCount(prod)} child product{getChildProductsCount(prod) !== 1 ? 's' : ''}
                        </span>
                        <button 
                          className="btn-add-child-full"
                          onClick={() => {
                            setCurrentParentProduct(prod.id);
                            setShowSubProductModal(true);
                          }}
                          style={{ position: 'relative' }}
                        >
                          <PlusCircle size={14} /> Add Child
                          <span className="tooltip-text">Add Child Product</span>
                        </button>
                      </div>
                      {prod.childProducts && prod.childProducts.map(child => (
                        <div key={child.id} className="child-product-item">
                          <span className="child-product-name">
                            {child.name} (Qty: {child.quantity})
                          </span>
                          <button 
                            className="btn-remove-item"
                            onClick={() => handleRemoveChildProduct(prod.id, child.id)}
                            style={{ position: 'relative' }}
                          >
                            Remove
                            <span className="tooltip-text">Remove Child Product</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="product-rm-actions">
                    <div className="product-action-group">
                      <div className="product-action-header-column">
                        <span className="product-action-count">
                          🔧 {getRawMaterialsCount(prod)} raw material{getRawMaterialsCount(prod) !== 1 ? 's' : ''}
                        </span>
                        <button 
                          className="btn-assign-rm-full"
                          onClick={() => {
                            setRawMaterialAssignment({ ...rawMaterialAssignment, productId: prod.id });
                            setShowRawMaterialModal(true);
                          }}
                          style={{ position: 'relative' }}
                        >
                          <Link2 size={14} /> Assign RM
                          <span className="tooltip-text">Assign Raw Material to Product</span>
                        </button>
                      </div>
                      {prod.rawMaterials && prod.rawMaterials.map(rm => (
                        <div key={rm.id} className="raw-material-item">
                          <span className="raw-material-name">
                            {rm.rawMaterial.name} - Qty: {rm.quantity}
                          </span>
                          <button 
                            className="btn-remove-item"
                            onClick={() => handleRemoveRawMaterial(prod.id, rm.id)}
                            style={{ position: 'relative' }}
                          >
                            Remove
                            <span className="tooltip-text">Remove Raw Material Assignment</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="action-buttons">
                    <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(`Product: ${prod.name}\nCode: ${prod.code}\nCost: ₹${prod.cost}\nChild Products: ${prod.childProducts?.length || 0}\nRaw Materials: ${prod.rawMaterials?.length || 0}`)} colorClass="view" />
                    <ActionIconButton icon={<Edit size={16} />} label="Edit Product" onClick={() => alert('Edit product: ' + prod.name)} colorClass="edit" />
                    <ActionIconButton icon={<Trash2 size={16} />} label="Delete Product" onClick={() => { if (window.confirm('Delete this product?')) setProducts(products.filter(p => p.id !== prod.id)); }} colorClass="delete" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CustomPagination 
          currentPage={productPage} 
          totalPages={Math.ceil(products.length / PAGE_SIZE)} 
          onPageChange={setProductPage} 
          totalItems={products.length} 
        />
      </div>

      {/* List Group */}
      <div className="list-section">
        <h3><Layers size={18} /> List Group ({groups.length})</h3>
        <div className="data-table-container">
          <table className="data-table">
            <thead><tr><th>Group Name</th><th>Description</th><th>Products</th><th>Actions</th></tr></thead>
            <tbody>
              {paginate(groups, groupPage).map(group => (
                <tr key={group.id}>
                  <td className="highlight">{formatValue(group.name)}</td>
                  <td>{formatValue(group.description)}</td>
                  <td>{group.products?.map(p => p.name).join(', ') || 'N/A'}</td>
                  <td className="action-buttons">
                    <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(`Group: ${group.name}\nProducts: ${group.products?.map(p => p.name).join(', ')}`)} colorClass="view" />
                    <ActionIconButton icon={<Edit size={16} />} label="Edit Group" onClick={() => alert('Edit group: ' + group.name)} colorClass="edit" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CustomPagination 
          currentPage={groupPage} 
          totalPages={Math.ceil(groups.length / PAGE_SIZE)} 
          onPageChange={setGroupPage} 
          totalItems={groups.length} 
        />
      </div>
    </div>
  );

  const renderCreatePO = () => {
    const customerIdError = getPOError('customerId');
    const supplierIdError = getPOError('supplierId');
    const deliveryDateError = getPOError('deliveryDate');
    const selectedProductsError = getPOError('selectedProducts');

    return (
      <div>
        <div className="form-section">
          <h3 className="form-section-title">Create New Purchase Order</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>PO Number</label>
              <input type="text" value={poForm.poNumber} disabled className="auto-field" />
              <small>Auto-generated</small>
            </div>
            <div className="form-group">
              <label>Order Date</label>
              <input type="date" value={poForm.orderDate} disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Select Customer <span className="required-star">*</span></label>
              <select value={poForm.customerId} onChange={(e) => setPoForm({...poForm, customerId: e.target.value})} className={customerIdError ? 'error' : ''}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </select>
              {customerIdError && <span className="error-text">{customerIdError}</span>}
            </div>
            <div className="form-group">
              <label>Select Supplier <span className="required-star">*</span></label>
              <select value={poForm.supplierId} onChange={(e) => setPoForm({...poForm, supplierId: e.target.value})} className={supplierIdError ? 'error' : ''}>
                <option value="">Select Supplier</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
              </select>
              {supplierIdError && <span className="error-text">{supplierIdError}</span>}
            </div>
            <div className="form-group">
              <label>Delivery Date <span className="required-star">*</span></label>
              <input type="date" value={poForm.deliveryDate} onChange={(e) => setPoForm({...poForm, deliveryDate: e.target.value})} className={deliveryDateError ? 'error' : ''} />
              {deliveryDateError && <span className="error-text">{deliveryDateError}</span>}
            </div>
          </div>

          <div className="stock-summary-section">
            <h4>Stock Summary</h4>
            <div className="stock-summary-grid">
              <div className="stock-card finished">
                <h5>Finished Goods</h5>
                {stockData.finishedGoods.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
              <div className="stock-card pending">
                <h5>Production Pending</h5>
                {stockData.productionPending.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
              <div className="stock-card outsource">
                <h5>Outsource Pending</h5>
                {stockData.outsourcePending.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
              <div className="stock-card raw">
                <h5>Raw Material</h5>
                {stockData.rawMaterial.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
            </div>
          </div>

          <div className="select-products-section">
            <h4>Select Products <span className="required-star">*</span></h4>
            <select className="form-input" onChange={(e) => handleAddProductToPO(parseInt(e.target.value))} value="">
              <option value="">-- Add Product --</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name} - ₹{p.cost}</option>)}
            </select>
            {selectedProductsError && <span className="error-text">{selectedProductsError}</span>}

            {poForm.selectedProducts.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4>Selected Products</h4>
                <div className="data-table-container">
                  <table className="products-mini-table">
                    <thead>
                      <tr><th>Product</th><th>Quantity</th><th>Unit Price</th><th>Total</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {poForm.selectedProducts.map(product => (
                        <tr key={product.id}>
                          <td>{formatValue(product.name)}</td>
                          <td><input type="number" value={product.quantity} onChange={(e) => handleUpdateProductQuantity(product.id, e.target.value)} style={{ width: '70px', padding: '4px' }} /></td>
                          <td>₹{formatValue(product.cost)}</td>
                          <td>₹{((product.cost || 0) * (product.quantity || 0)).toLocaleString()}</td>
                          <td><ActionIconButton icon={<Trash2 size={16} />} label="Remove Product" onClick={() => handleRemoveProductFromPO(product.id)} colorClass="delete" /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan="3"><strong>Total Amount</strong></td><td><strong>₹{poForm.totalAmount.toLocaleString()}</strong></td><td></td></tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions" style={{ marginTop: '24px', padding: '0' }}>
            <TooltipButton className="btn-submit" label="Create Purchase Order" onClick={handleCreatePO}>
              Create PO
            </TooltipButton>
          </div>
        </div>
      </div>
    );
  };

  const renderListPO = () => {
    const filtered = filteredData(purchaseOrders, ['poNumber', 'customer.companyName', 'supplier.companyName']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, poPage);

    return (
      <div>
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by PO Number, Customer, Supplier..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <ActionIconButton icon={<Filter size={16} />} label="Filter" onClick={() => setFilterOpen(!filterOpen)} colorClass="view" />
        </div>

        {purchaseOrders.length === 0 ? (
          <div className="empty-state"><List size={48} color="#CBD5E1" /><p>No purchase orders found</p></div>
        ) : (
          <>
            <div className="po-cards-grid">
              {pageData.map(po => (
                <div key={po.id} className="po-card">
                  <div className="po-card-header">
                    <h3>{formatValue(po.poNumber)}</h3>
                    <span className={`status-badge ${po.status?.toLowerCase() === 'approved' ? 'approved' : 'pending'}`}>{formatValue(po.status)}</span>
                  </div>
                  <div className="po-card-details">
                    <div><label>Customer</label><span>{po.customer?.companyName || 'N/A'}</span></div>
                    <div><label>Supplier</label><span>{po.supplier?.companyName || 'N/A'}</span></div>
                    <div><label>Order Date</label><span>{formatValue(po.orderDate)}</span></div>
                    <div><label>Delivery Date</label><span>{formatValue(po.deliveryDate)}</span></div>
                    <div><label>Total Amount</label><span className="amount">₹{formatValue(po.totalAmount)}</span></div>
                    <div><label>Products</label><span>{po.selectedProducts?.map(p => `${p.name}(${p.quantity})`).join(', ') || 'N/A'}</span></div>
                  </div>
                  <div className="po-card-actions">
                    <TooltipButton className="btn-approve" label="Approve Order" onClick={() => {
                      const updatedOrders = purchaseOrders.map(o => o.id === po.id ? { ...o, status: 'Approved' } : o);
                      setPurchaseOrders(updatedOrders);
                    }}>
                      <CheckCircle size={16} /> Approve
                    </TooltipButton>
                    <TooltipButton className="btn-edit" label="Edit Order" onClick={() => alert('Edit PO: ' + po.poNumber)}>
                      <Edit size={16} /> Edit
                    </TooltipButton>
                  </div>
                </div>
              ))}
            </div>
            <CustomPagination 
              currentPage={poPage} 
              totalPages={totalPages} 
              onPageChange={setPoPage} 
              totalItems={filtered.length} 
            />
          </>
        )}
      </div>
    );
  };

  const renderListGRN = () => {
    const filtered = filteredData(grns, ['grnNumber', 'poNumber', 'supplier', 'product']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, grnPage);

    return (
      <div>
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search GRN by Number, PO, Supplier..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {grns.length === 0 ? (
          <div className="empty-state"><List size={48} color="#CBD5E1" /><p>No GRN records found</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>GRN Number</th><th>PO Number</th><th>Supplier</th><th>Product</th><th>Quantity</th><th>Supplier Invoice No</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(grn => (
                    <tr key={grn.id}>
                      <td className="highlight">{formatValue(grn.grnNumber)}</td>
                      <td>{formatValue(grn.poNumber)}</td>
                      <td>{formatValue(grn.supplier)}</td>
                      <td>{formatValue(grn.product)}</td>
                      <td>{formatValue(grn.quantity)}</td>
                      <td>{formatValue(grn.supplierInvoiceNo)}</td>
                      <td><span className={`status-badge ${grn.status?.toLowerCase() === 'approved' ? 'approved' : 'pending'}`}>{formatValue(grn.status)}</span></td>
                      <td className="action-buttons">
                        <TooltipButton className="btn-approve" label="Submit GRN" onClick={() => alert('Submit GRN: ' + grn.grnNumber)}>
                          Submit
                        </TooltipButton>
                        <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(grn, null, 2))} colorClass="view" />
                        <ActionIconButton icon={<Edit size={16} />} label="Edit GRN" onClick={() => alert('Edit GRN: ' + grn.grnNumber)} colorClass="edit" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination 
              currentPage={grnPage} 
              totalPages={totalPages} 
              onPageChange={setGrnPage} 
              totalItems={filtered.length} 
            />
          </>
        )}
      </div>
    );
  };

  const renderSupplierPayment = () => {
    const grnIdError = getPaymentError('grnId');
    const amountError = getPaymentError('amount');
    const paymentModeError = getPaymentError('paymentMode');
    const utrNumberError = getPaymentError('utrNumber');

    const filtered = filteredData(payments, ['paymentId', 'grnId', 'utrNumber']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, paymentPage);

    return (
      <div>
        <div className="form-section">
          <h3 className="form-section-title">Make Supplier Payment</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Payment ID</label>
              <input type="text" value={paymentForm.paymentId} disabled className="auto-field" />
              <small>Auto-generated</small>
            </div>
            <div className="form-group">
              <label>Payment Date</label>
              <input type="date" value={paymentForm.paymentDate} onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Select GRN <span className="required-star">*</span></label>
              <select value={paymentForm.grnId} onChange={(e) => setPaymentForm({...paymentForm, grnId: e.target.value})} className={grnIdError ? 'error' : ''}>
                <option value="">Select GRN</option>
                {grns.map(g => <option key={g.id} value={g.grnNumber}>{g.grnNumber} - {g.supplier} - ₹{g.quantity * 1000}</option>)}
              </select>
              {grnIdError && <span className="error-text">{grnIdError}</span>}
            </div>
            <div className="form-group">
              <label>Amount <span className="required-star">*</span></label>
              <input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})} className={amountError ? 'error' : ''} placeholder="Enter amount" />
              {amountError && <span className="error-text">{amountError}</span>}
            </div>
            <div className="form-group">
              <label>Payment Mode <span className="required-star">*</span></label>
              <select value={paymentForm.paymentMode} onChange={(e) => setPaymentForm({...paymentForm, paymentMode: e.target.value})} className={paymentModeError ? 'error' : ''}>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Cash">Cash</option>
              </select>
              {paymentModeError && <span className="error-text">{paymentModeError}</span>}
            </div>
            <div className="form-group">
              <label>UTR Number <span className="required-star">*</span></label>
              <input type="text" value={paymentForm.utrNumber} onChange={(e) => setPaymentForm({...paymentForm, utrNumber: e.target.value})} className={utrNumberError ? 'error' : ''} placeholder="Enter UTR number" />
              {utrNumberError && <span className="error-text">{utrNumberError}</span>}
            </div>
            <div className="form-group">
              <label>Verified</label>
              <input type="checkbox" checked={paymentForm.verified} onChange={(e) => setPaymentForm({...paymentForm, verified: e.target.checked})} style={{ width: 'auto' }} />
            </div>
          </div>
          <div className="form-actions" style={{ marginTop: '20px', padding: '0' }}>
            <TooltipButton className="btn-submit" label="Record Payment" onClick={handleMakePayment}>
              Submit Payment
            </TooltipButton>
          </div>
        </div>

        <div className="list-section">
          <h3><DollarSign size={18} /> Payment Records ({payments.length})</h3>
          {payments.length === 0 ? (
            <div className="empty-state"><DollarSign size={48} color="#CBD5E1" /><p>No payment records found</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>Payment ID</th><th>GRN</th><th>Amount</th><th>Payment Mode</th><th>Date</th><th>UTR Number</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {pageData.map(payment => (
                      <tr key={payment.id}>
                        <td className="highlight">{formatValue(payment.paymentId)}</td>
                        <td>{formatValue(payment.grnId)}</td>
                        <td className="amount">₹{formatValue(payment.amount)}</td>
                        <td>{formatValue(payment.paymentMode)}</td>
                        <td>{formatValue(payment.paymentDate)}</td>
                        <td>{formatValue(payment.utrNumber)}</td>
                        <td><span className={`status-badge ${payment.verified ? 'approved' : 'pending'}`}>{payment.verified ? 'Verified' : 'Pending'}</span></td>
                        <td className="action-buttons">
                          {!payment.verified && (
                            <TooltipButton className="btn-approve" label="Verify Payment" onClick={() => {
                              const updatedPayments = payments.map(p => p.id === payment.id ? { ...p, verified: true } : p);
                              setPayments(updatedPayments);
                            }}>
                              <CheckCircle size={16} /> Verify
                            </TooltipButton>
                          )}
                          <ActionIconButton icon={<Edit size={16} />} label="Edit Payment" onClick={() => alert('Edit payment: ' + payment.paymentId)} colorClass="edit" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CustomPagination 
                currentPage={paymentPage} 
                totalPages={totalPages} 
                onPageChange={setPaymentPage} 
                totalItems={filtered.length} 
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderListSchedule = () => {
    const filtered = filteredData(schedules, ['category', 'customer', 'productName']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, schedulePage);

    return (
      <div>
        <div className="form-section">
          <h3 className="form-section-title">Create New Schedule</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Category <span className="required-star">*</span></label>
              <select value={scheduleForm.category} onChange={(e) => setScheduleForm({...scheduleForm, category: e.target.value})}>
                <option value="">Select Category</option>
                {categories.map(c => <option key={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Sub Category <span className="optional-text">(Optional)</span></label>
              <input type="text" value={scheduleForm.subCategory} onChange={(e) => setScheduleForm({...scheduleForm, subCategory: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Group <span className="optional-text">(Optional)</span></label>
              <select value={scheduleForm.group} onChange={(e) => setScheduleForm({...scheduleForm, group: e.target.value})}>
                <option value="">Select Group</option>
                {groups.map(g => <option key={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Bought from customer <span className="required-star">*</span></label>
              <select value={scheduleForm.customer} onChange={(e) => setScheduleForm({...scheduleForm, customer: e.target.value})}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id}>{c.companyName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Product Name <span className="required-star">*</span></label>
              <select value={scheduleForm.productId} onChange={(e) => setScheduleForm({...scheduleForm, productId: e.target.value})}>
                <option value="">Select Product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>No of Quantity <span className="required-star">*</span></label>
              <input type="number" value={scheduleForm.quantity} onChange={(e) => setScheduleForm({...scheduleForm, quantity: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Frequency <span className="required-star">*</span></label>
              <select value={scheduleForm.frequency} onChange={(e) => setScheduleForm({...scheduleForm, frequency: e.target.value})}>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </div>
            <div className="form-group">
              <label>Valid Till date <span className="required-star">*</span></label>
              <input type="date" value={scheduleForm.validTill} onChange={(e) => setScheduleForm({...scheduleForm, validTill: e.target.value})} />
            </div>
          </div>
          <div className="form-actions" style={{ marginTop: '20px', padding: '0' }}>
            <TooltipButton className="btn-submit" label="Create Schedule" onClick={handleCreateSchedule}>
              Create Schedule
            </TooltipButton>
          </div>
        </div>

        <div className="stock-summary-section">
          <h4>Stock Summary</h4>
          <div className="stock-summary-grid">
            <div className="stock-card finished"><h5>Finished Goods</h5>{stockData.finishedGoods.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}</div>
            <div className="stock-card pending"><h5>Production Pending</h5>{stockData.productionPending.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}</div>
            <div className="stock-card outsource"><h5>Outsource Pending</h5>{stockData.outsourcePending.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}</div>
            <div className="stock-card raw"><h5>Raw Material</h5>{stockData.rawMaterial.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}</div>
          </div>
        </div>

        <div className="list-section">
          <h3><Calendar size={18} /> Existing Schedules ({schedules.length})</h3>
          {schedules.length === 0 ? (
            <div className="empty-state"><Calendar size={48} color="#CBD5E1" /><p>No schedules available. Create one above.</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead><tr><th>Product</th><th>Category</th><th>Customer</th><th>Quantity</th><th>Frequency</th><th>Valid Till</th><th>Actions</th></tr></thead>
                  <tbody>
                    {pageData.map(schedule => (
                      <tr key={schedule.id}>
                        <td className="highlight">{formatValue(schedule.productName)}</td>
                        <td>{formatValue(schedule.category)}</td>
                        <td>{formatValue(schedule.customer)}</td>
                        <td>{formatValue(schedule.quantity)}</td>
                        <td>{formatValue(schedule.frequency)}</td>
                        <td>{formatValue(schedule.validTill)}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(schedule, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit Schedule" onClick={() => alert('Edit schedule')} colorClass="edit" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CustomPagination 
                currentPage={schedulePage} 
                totalPages={totalPages} 
                onPageChange={setSchedulePage} 
                totalItems={filtered.length} 
              />
            </>
          )}
        </div>
      </div>
    );
  };

  // Modal render functions (same as before, just replace buttons with TooltipButton)
  const renderCreateProductModal = () => (
    <ProductModal
      isOpen={showCreateProduct}
      title="Add Product"
      submitLabel="Add Product"
      onClose={() => { setShowCreateProduct(false); setProductSubmitAttempted(false); }}
      onSubmit={handleCreateProduct}
      productForm={productForm}
      setProductForm={setProductForm}
      errors={{
        name: getProductError('name'),
        code: getProductError('code'),
        category: getProductError('category'),
        cost: getProductError('cost')
      }}
      categories={categories}
      rawMaterials={rawMaterials}
      rawMaterialAssignment={rawMaterialAssignment}
      setRawMaterialAssignment={setRawMaterialAssignment}
      extraFields={productExtraFields}
      onAddExtraField={() => addExtraField('product')}
      onUpdateExtraField={(index, field, value) => updateExtraField('product', index, field, value)}
      onRemoveExtraField={(index) => removeExtraField('product', index)}
      onFileChange={handleFileChange}
      onCalculateTotalDimension={calculateTotalDimension}
    />
  );

  const renderSubProductModal = () => {
    return showSubProductModal && (
      <div className="modal-overlay" onClick={() => { setShowSubProductModal(false); setChildProductForm({ name: '', code: '', description: '', quantity: 1, rawMaterials: [] }); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add Child / Sub Product</h2>
            <button className="modal-close" onClick={() => { setShowSubProductModal(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Child Product Name <span className="required-star">*</span></label>
                <input type="text" value={childProductForm.name} onChange={(e) => setChildProductForm({...childProductForm, name: e.target.value})} placeholder="Enter child product name" />
              </div>
              <div className="form-group">
                <label>Child Product Code</label>
                <input type="text" value={childProductForm.code} onChange={(e) => setChildProductForm({...childProductForm, code: e.target.value})} placeholder="Enter child product code" />
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea rows="2" value={childProductForm.description} onChange={(e) => setChildProductForm({...childProductForm, description: e.target.value})} placeholder="Enter description" />
              </div>
              <div className="form-group">
                <label>Quantity Required</label>
                <input type="number" value={childProductForm.quantity} onChange={(e) => setChildProductForm({...childProductForm, quantity: parseInt(e.target.value) || 1})} />
              </div>
            </div>
            <div style={{ marginTop: '20px', padding: '16px', background: '#FEF3C7', borderRadius: '12px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>🔗 Select Raw Material for Child Product</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Raw Material Type</label>
                  <select value={childProductForm.rawMaterials[0]?.id || ''} onChange={(e) => {
                    const selectedRM = rawMaterials.find(rm => rm.id === parseInt(e.target.value));
                    if (selectedRM) {
                      setChildProductForm({ ...childProductForm, rawMaterials: [{ ...selectedRM, quantity: 1 }] });
                    }
                  }}>
                    <option value="">Select Raw Material</option>
                    {rawMaterials.map(rm => <option key={rm.id} value={rm.id}>{rm.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantity</label>
                  <input type="number" value={childProductForm.rawMaterials[0]?.quantity || 1} onChange={(e) => {
                    const updatedRM = [...childProductForm.rawMaterials];
                    if (updatedRM[0]) updatedRM[0].quantity = parseInt(e.target.value) || 1;
                    setChildProductForm({ ...childProductForm, rawMaterials: updatedRM });
                  }} />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <TooltipButton className="btn-cancel" label="Cancel" onClick={() => { setShowSubProductModal(false); }}>
              Cancel
            </TooltipButton>
            <TooltipButton className="btn-submit" label="Add Child Product" onClick={handleAddChildProduct}>
              Add Child Product
            </TooltipButton>
          </div>
        </div>
      </div>
    );
  };

  const renderRawMaterialModal = () => {
    return showRawMaterialModal && (
      <div className="modal-overlay" onClick={() => { setShowRawMaterialModal(false); setRawMaterialAssignment({ productId: null, rawMaterialId: '', quantity: 1, area: 0 }); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Assign Raw Material to Product</h2>
            <button className="modal-close" onClick={() => { setShowRawMaterialModal(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Raw Material Type <span className="required-star">*</span></label>
                <select value={rawMaterialAssignment.rawMaterialId} onChange={(e) => setRawMaterialAssignment({...rawMaterialAssignment, rawMaterialId: e.target.value})}>
                  <option value="">Select Raw Material</option>
                  {rawMaterials.map(rm => <option key={rm.id} value={rm.id}>{rm.name} - ₹{rm.cost}/{rm.unit}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Quantity <span className="required-star">*</span></label>
                <input type="number" value={rawMaterialAssignment.quantity} onChange={(e) => setRawMaterialAssignment({...rawMaterialAssignment, quantity: parseInt(e.target.value) || 1})} placeholder="Enter quantity" />
              </div>
              <div className="form-group">
                <label>Area (sq mm)</label>
                <input type="number" value={rawMaterialAssignment.area} onChange={(e) => setRawMaterialAssignment({...rawMaterialAssignment, area: parseFloat(e.target.value) || 0})} placeholder="Enter area if applicable" />
              </div>
            </div>
            <div style={{ marginTop: '16px', padding: '12px', background: '#F1F5F9', borderRadius: '8px' }}>
              <p style={{ fontSize: '12px', color: '#64748B' }}><strong>RM vs Parts:</strong> This raw material will be linked to the selected product for tracking purposes.</p>
            </div>
          </div>
          <div className="modal-footer">
            <TooltipButton className="btn-cancel" label="Cancel" onClick={() => { setShowRawMaterialModal(false); }}>
              Cancel
            </TooltipButton>
            <TooltipButton className="btn-submit" label="Assign Raw Material" onClick={handleAssignRawMaterial}>
              Assign Raw Material
            </TooltipButton>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateCustomerModal = () => {
    const pocNameError = getCustomerError('pocName');
    const companyNameError = getCustomerError('companyName');
    const usernameError = getCustomerError('username');
    const passwordError = getCustomerError('password');
    const addressError = getCustomerError('address');

    return showCreateCustomer && (
      <div className="modal-overlay" onClick={handleCloseCustomerModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Customer</h2>
            <button className="modal-close" onClick={handleCloseCustomerModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>POC Name <span className="required-star">*</span></label>
                <input type="text" value={customerForm.pocName} onChange={(e) => setCustomerForm({...customerForm, pocName: e.target.value})} className={pocNameError ? 'error' : ''} />
                {pocNameError && <span className="error-text">{pocNameError}</span>}
              </div>
              <div className="form-group">
                <label>Company Name <span className="required-star">*</span></label>
                <input type="text" value={customerForm.companyName} onChange={(e) => setCustomerForm({...customerForm, companyName: e.target.value})} className={companyNameError ? 'error' : ''} />
                {companyNameError && <span className="error-text">{companyNameError}</span>}
              </div>
              <div className="form-group">
                <label>Username <span className="required-star">*</span></label>
                <input type="text" value={customerForm.username} onChange={(e) => setCustomerForm({...customerForm, username: e.target.value})} className={usernameError ? 'error' : ''} />
                {usernameError && <span className="error-text">{usernameError}</span>}
              </div>
              <div className="form-group">
                <label>Password <span className="required-star">*</span></label>
                <input type="password" value={customerForm.password} onChange={(e) => setCustomerForm({...customerForm, password: e.target.value})} className={passwordError ? 'error' : ''} />
                {passwordError && <span className="error-text">{passwordError}</span>}
              </div>
              <div className="form-group full-width">
                <label>Address <span className="required-star">*</span></label>
                <textarea rows="3" value={customerForm.address} onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})} className={addressError ? 'error' : ''} placeholder="Enter complete address" />
                {addressError && <span className="error-text">{addressError}</span>}
              </div>
              <div className="form-group">
                <label>PAN <span className="optional-text">(Optional)</span></label>
                <input type="text" value={customerForm.pan} onChange={(e) => setCustomerForm({...customerForm, pan: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Bank Details <span className="optional-text">(Optional)</span></label>
                <input type="text" value={customerForm.bankDetails} onChange={(e) => setCustomerForm({...customerForm, bankDetails: e.target.value})} />
              </div>
              <div className="form-group">
                <label>GST <span className="optional-text">(Optional)</span></label>
                <input type="text" value={customerForm.gst} onChange={(e) => setCustomerForm({...customerForm, gst: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Vendor Code</label>
                <input type="text" value={customerForm.vendorCode} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
            </div>

            <div className="long-text-fields-section">
              <h4>Additional Information</h4>
              <div className="form-group full-width">
                <label>Special Instructions / Notes <span className="optional-text">(Optional)</span></label>
                <textarea rows="3" placeholder="Enter any special instructions, notes, or additional information about this customer..." />
              </div>
            </div>

            <div className="extra-fields-section">
              <label>Add Extra Fields <span className="optional-text">(Optional)</span></label>
              {customerExtraFields.map((field, index) => (
                <div key={index} className="extra-field-row">
                  <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('customer', index, 'name', e.target.value)} className="extra-field-input" />
                  <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('customer', index, 'value', e.target.value)} className="extra-field-input" />
                  <button onClick={() => removeExtraField('customer', index)} className="remove-field-btn"><X size={16} /></button>
                </div>
              ))}
              <button className="add-field-btn" onClick={() => addExtraField('customer')}><PlusCircle size={14} /> Add field</button>
            </div>
          </div>
          <div className="modal-footer">
            <TooltipButton className="btn-cancel" label="Cancel" onClick={handleCloseCustomerModal}>
              Cancel
            </TooltipButton>
            <TooltipButton className="btn-submit" label="Submit Customer" onClick={handleCreateCustomer}>
              Submit
            </TooltipButton>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateSupplierModal = () => {
    const pocNameError = getSupplierError('pocName');
    const companyNameError = getSupplierError('companyName');
    const usernameError = getSupplierError('username');
    const passwordError = getSupplierError('password');
    const addressError = getSupplierError('address');

    return showCreateSupplier && (
      <div className="modal-overlay" onClick={handleCloseSupplierModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Supplier</h2>
            <button className="modal-close" onClick={handleCloseSupplierModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>POC Name <span className="required-star">*</span></label>
                <input type="text" value={supplierForm.pocName} onChange={(e) => setSupplierForm({...supplierForm, pocName: e.target.value})} className={pocNameError ? 'error' : ''} />
                {pocNameError && <span className="error-text">{pocNameError}</span>}
              </div>
              <div className="form-group">
                <label>Company Name <span className="required-star">*</span></label>
                <input type="text" value={supplierForm.companyName} onChange={(e) => setSupplierForm({...supplierForm, companyName: e.target.value})} className={companyNameError ? 'error' : ''} />
                {companyNameError && <span className="error-text">{companyNameError}</span>}
              </div>
              <div className="form-group">
                <label>Username <span className="required-star">*</span></label>
                <input type="text" value={supplierForm.username} onChange={(e) => setSupplierForm({...supplierForm, username: e.target.value})} className={usernameError ? 'error' : ''} />
                {usernameError && <span className="error-text">{usernameError}</span>}
              </div>
              <div className="form-group">
                <label>Password <span className="required-star">*</span></label>
                <input type="password" value={supplierForm.password} onChange={(e) => setSupplierForm({...supplierForm, password: e.target.value})} className={passwordError ? 'error' : ''} />
                {passwordError && <span className="error-text">{passwordError}</span>}
              </div>
              <div className="form-group full-width">
                <label>Address <span className="required-star">*</span></label>
                <textarea rows="3" value={supplierForm.address} onChange={(e) => setSupplierForm({...supplierForm, address: e.target.value})} className={addressError ? 'error' : ''} placeholder="Enter complete address" />
                {addressError && <span className="error-text">{addressError}</span>}
              </div>
              <div className="form-group">
                <label>PAN <span className="optional-text">(Optional)</span></label>
                <input type="text" value={supplierForm.pan} onChange={(e) => setSupplierForm({...supplierForm, pan: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Bank Details <span className="optional-text">(Optional)</span></label>
                <input type="text" value={supplierForm.bankDetails} onChange={(e) => setSupplierForm({...supplierForm, bankDetails: e.target.value})} />
              </div>
              <div className="form-group">
                <label>GST <span className="optional-text">(Optional)</span></label>
                <input type="text" value={supplierForm.gst} onChange={(e) => setSupplierForm({...supplierForm, gst: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Supplier Code</label>
                <input type="text" value={supplierForm.supplierCode} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
            </div>

            <div className="long-text-fields-section">
              <h4>Additional Information</h4>
              <div className="form-group full-width">
                <label>Special Instructions / Notes <span className="optional-text">(Optional)</span></label>
                <textarea rows="3" placeholder="Enter any special instructions, notes, or additional information about this supplier..." />
              </div>
            </div>

            <div className="extra-fields-section">
              <label>Add Extra Fields <span className="optional-text">(Optional)</span></label>
              {supplierExtraFields.map((field, index) => (
                <div key={index} className="extra-field-row">
                  <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('supplier', index, 'name', e.target.value)} className="extra-field-input" />
                  <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('supplier', index, 'value', e.target.value)} className="extra-field-input" />
                  <button onClick={() => removeExtraField('supplier', index)} className="remove-field-btn"><X size={16} /></button>
                </div>
              ))}
              <button className="add-field-btn" onClick={() => addExtraField('supplier')}><PlusCircle size={14} /> Add field</button>
            </div>
          </div>
          <div className="modal-footer">
            <TooltipButton className="btn-cancel" label="Cancel" onClick={handleCloseSupplierModal}>
              Cancel
            </TooltipButton>
            <TooltipButton className="btn-submit" label="Submit Supplier" onClick={handleCreateSupplier}>
              Submit
            </TooltipButton>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateCategoryModal = () => {
    const nameError = getCategoryError('name');
    return showCreateCategory && (
      <div className="modal-overlay" onClick={() => { setShowCreateCategory(false); setCategorySubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add Category</h2>
            <button className="modal-close" onClick={() => { setShowCreateCategory(false); setCategorySubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label>Category Name <span className="required-star">*</span></label>
              <input type="text" value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} className={nameError ? 'error' : ''} placeholder="Enter category name" />
              {nameError && <span className="error-text">{nameError}</span>}
            </div>
            <div className="form-group">
              <label>Description <span className="optional-text">(Optional)</span></label>
              <textarea rows="2" value={categoryForm.description} onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})} placeholder="Enter description" />
            </div>
          </div>
          <div className="modal-footer">
            <TooltipButton className="btn-cancel" label="Cancel" onClick={() => { setShowCreateCategory(false); setCategorySubmitAttempted(false); }}>
              Cancel
            </TooltipButton>
            <TooltipButton className="btn-submit" label="Add Category" onClick={handleCreateCategory}>
              Add
            </TooltipButton>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateGroupModal = () => {
    const nameError = getGroupError('name');
    const productsError = getGroupError('products');
    return showCreateGroup && (
      <div className="modal-overlay" onClick={() => { setShowCreateGroup(false); setGroupSubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Group</h2>
            <button className="modal-close" onClick={() => { setShowCreateGroup(false); setGroupSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label>Group Name <span className="required-star">*</span></label>
              <input type="text" value={groupForm.name} onChange={(e) => setGroupForm({...groupForm, name: e.target.value})} className={nameError ? 'error' : ''} placeholder="Enter group name" />
              {nameError && <span className="error-text">{nameError}</span>}
            </div>
            <div className="form-group">
              <label>Description <span className="optional-text">(Optional)</span></label>
              <textarea rows="2" value={groupForm.description} onChange={(e) => setGroupForm({...groupForm, description: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Select Products <span className="required-star">*</span></label>
              <select multiple size="5" value={groupForm.products} onChange={(e) => setGroupForm({...groupForm, products: Array.from(e.target.selectedOptions, o => o.value)})} className={productsError ? 'error' : ''}>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {productsError && <span className="error-text">{productsError}</span>}
              <small>Hold Ctrl/Cmd to select multiple products</small>
            </div>
          </div>
          <div className="modal-footer">
            <TooltipButton className="btn-cancel" label="Cancel" onClick={() => { setShowCreateGroup(false); setGroupSubmitAttempted(false); }}>
              Cancel
            </TooltipButton>
            <TooltipButton className="btn-submit" label="Create Group" onClick={handleCreateGroup}>
              Add
            </TooltipButton>
          </div>
        </div>
      </div>
    );
  };

  // Main render
  const renderContent = () => {
    switch(activeTab) {
      case "create-ledger": return renderCreateLedger();
      case "product": return renderProduct();
      case "create-po": return renderCreatePO();
      case "list-po": return renderListPO();
      case "list-grn": return renderListGRN();
      case "supplier-payment": return renderSupplierPayment();
      case "list-schedule": return renderListSchedule();
      default: return renderCreateLedger();
    }
  };

  return (
    <div className="purchase-dashboard-container">
      {/* Sidebar */}
      <div className="purchase-sidebar">
        <div className="purchase-logo-container">
          <div className="purchase-logo-icon">
            <img src="/rahulerp.jpg" alt="Logo" className="purchase-logo-image" onError={(e) => { e.target.src = 'https://via.placeholder.com/48x48?text=RAT'; }} />
          </div>
          <div>
            <h2 className="purchase-company-name">Ragul Auto Tech</h2>
            <p className="purchase-company-tagline">AI Powered ERP</p>
          </div>
        </div>

        <nav className="purchase-nav-menu">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              className={`purchase-nav-item ${activeTab === tab.id ? 'purchase-nav-item-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
            >
              <span className="purchase-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="purchase-logout-container">
          <TooltipButton onClick={handleLogoutClick} className="purchase-logout-button" label="Logout from Purchase Dashboard">
            <LogOut size={18} /> Logout
          </TooltipButton>
        </div>
      </div>

      {/* Main Content */}
      <div className="purchase-main-content">
        <ScreenHeader
          title="Purchase Dashboard"
          description={`Welcome, ${user?.role || 'Purchase Manager'}`}
          timestamp={new Date().toLocaleString()}
          containerClassName="purchase-header"
          titleClassName="purchase-main-title"
          descriptionClassName="purchase-subtitle"
          actionsClassName="purchase-header-actions"
          timestampClassName="purchase-last-updated"
        />
        <div className="purchase-back-button-wrapper">
          <BackButton />
        </div>

        <div className="purchase-scrollable-content">
          <div className="purchase-content-card">
            {renderContent()}
          </div>
        </div>

        {/* Logout Modal */}
        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header">
                <div className="logout-modal-icon">👋</div>
                <h3>Confirm Logout</h3>
              </div>
              <div className="logout-modal-body">
                <p>Are you sure you want to logout from <strong>Purchase</strong> dashboard?</p>
                <p className="logout-modal-warning">You will be redirected to the login page.</p>
              </div>
              <div className="logout-modal-footer">
                <TooltipButton className="logout-modal-cancel" label="Cancel Logout" onClick={handleCancelLogout}>
                  Cancel
                </TooltipButton>
                <TooltipButton className="logout-modal-confirm" label="Confirm Logout" onClick={handleConfirmLogout}>
                  Yes, Logout
                </TooltipButton>
              </div>
            </div>
          </div>
        )}

        {/* All Modals */}
        {renderCreateCustomerModal()}
        {renderCreateSupplierModal()}
        {renderCreateCategoryModal()}
        {renderCreateProductModal()}
        {renderCreateGroupModal()}
        {renderSubProductModal()}
        {renderRawMaterialModal()}
      </div>
    </div>
  );
}